import { useEffect } from 'react';
import { TableChangeSource, TableContentData } from '../../../../common/table/TableTypes';
import { ActiveTableSession, FlushReason } from '../store/hotTableStoreTypes';
import usePrevious from '../../../utils/react/usePrevious';
import { useHotToReduxSessionControls } from './useHotToReduxSessionControls';
import { registerMilanoteHotHooks } from './milanoteHotToReduxSessionHooks';
import { ON_MILANOTE_MARK_DIRTY, ON_MILANOTE_MARK_DIRTY_AND_FLUSH } from './milanoteHotToReduxSessionHookNames';
import { MilanoteHotEvents, MNHotInstance } from '../tableHotInstanceTypes';

export { ACTIVE_TABLE_FLUSH_DEBOUNCE_MS } from './useHotToReduxSessionControls';

registerMilanoteHotHooks();

type UseHotToReduxSessionProps = {
    elementId: string;
    tableInstance: MNHotInstance | null;
    isActive: boolean;
    isResizing: boolean;
    gridSize: number;
    elementWidth: number | undefined;
    tableContentData: TableContentData | undefined;
    tableContentColWidthsGU: number[] | undefined;
};

/**
 * Tracks active-table persistence state for tables.
 * This hook listens to all HoT instance events that trigger a change to the data that will need to be persisted.
 *
 * It then uses debounce logic to flush the changes after a period of inactivity.
 *
 * It also returns "session functions" that will allow external components to imperatively flush the active table
 * to the redux store.
 *
 * This is the authoritative HOT -> Redux persistence path for active V2 tables.
 */
export const useHotToReduxSession = ({
    elementId,
    tableInstance,
    isActive,
    isResizing,
    gridSize,
    elementWidth,
    tableContentData,
    tableContentColWidthsGU,
}: UseHotToReduxSessionProps): ActiveTableSession => {
    const prevIsActive = usePrevious(isActive);
    const prevIsResizing = usePrevious(isResizing);

    const session = useHotToReduxSessionControls({
        elementId,
        tableInstance,
        gridSize,
        elementWidth,
        tableContentData,
        tableContentColWidthsGU,
    });

    /**
     * After resizing an element is complete, we want to immediately flush the update to ensure undo & redo
     * feels right.
     */
    useEffect(() => {
        // If still resizing don't update
        if (!prevIsResizing || isResizing) return;

        session.markDirtyAndFlush();
    }, [isResizing, prevIsResizing, session]);

    /**
     * When the active editing session ends, immediately persist any pending dirty state before teardown.
     */
    useEffect(() => {
        if (!prevIsActive || isActive) return;

        if (!session.isDirty()) return;

        session.markDirtyAndFlush();
    }, [isActive, prevIsActive, session]);

    /**
     * Attach HoT event callbacks to trigger debounced redux state updates.
     */
    useEffect(() => {
        if (!tableInstance) return;

        // Centralize the "mutation happened" response so all observed HOT callbacks feed the same dirty/debounce path.
        const observeMutation = (reason?: FlushReason) => {
            session.markDirty(reason);
        };

        // Some mutations require the redux store to be immediately updated (e.g. column width changes)
        //  due to rendering occurring in both Milanote (element widths) and Handsontable (column widths).
        const immediatelySyncMutation = () => {
            session.markDirtyAndFlush();
        };

        /**
         * Milanote-specific hooks can pass through a flush reason because they are triggered manually.
         */
        const observeImmediateMutation = (reason?: FlushReason) => {
            session.markDirtyAndFlush(reason);
        };

        const afterMove = (movePossible: boolean, orderChanged: boolean, reason: FlushReason) => {
            if (!movePossible || !orderChanged) return;

            observeMutation(reason);
        };

        const afterGridAltered = (source?: string) => {
            if (source === TableChangeSource.LOAD_DATA) return;
            // This change will be immediately synced instead - handled in a useEffect above (note, if we handled
            //  it here, it would trigger an update on every column addition)
            if (source === TableChangeSource.RESIZE_HANDLE) return;

            return immediatelySyncMutation();
        };

        const callbacks: MilanoteHotEvents = {
            afterChange: (changes, source) => {
                if (!changes?.length) return;
                if (source === TableChangeSource.LOAD_DATA) return;
                // NOTE: Incorrect Handsontable type - source is "updateData" when hotInstance.updateData is used
                if ((source as TableChangeSource) === TableChangeSource.UPDATE_DATA) return;

                observeMutation(`afterChange:${source ?? 'unknown'}`);
            },
            afterCreateRow: (_index, _amount, source) => afterGridAltered(source),
            afterCreateCol: (_index, _amount, source) => afterGridAltered(source),
            afterRemoveRow: (_index, _amount, _removedRows, source) => afterGridAltered(source),
            afterRemoveCol: (_index, _amount, _logicColumns, source) => afterGridAltered(source),
            afterRowMove: (_movedRows, _finalIndex, _dropIndex, movePossible, orderChanged) =>
                afterMove(movePossible, orderChanged, 'afterRowMove'),
            afterColumnMove: (_movedColumns, _finalIndex, _dropIndex, movePossible, orderChanged) =>
                afterMove(movePossible, orderChanged, 'afterColumnMove'),
            // Column resizes need to be updated immediately, otherwise the size of the table might not match
            //  the size on the canvas (i.e. if double click is used to trigger the update, then the width
            //  of the table element will be larger than the width of the rendered table momentarily).
            afterColumnResize: immediatelySyncMutation,
            [ON_MILANOTE_MARK_DIRTY]: observeMutation,
            [ON_MILANOTE_MARK_DIRTY_AND_FLUSH]: observeImmediateMutation,
        };

        // Attach event listeners
        Object.entries(callbacks).forEach(([name, callback]) => {
            tableInstance.addHook(name as keyof MilanoteHotEvents, callback);
        });

        // Cleanup event listeners
        return () => {
            Object.entries(callbacks).forEach(([name, callback]) => {
                tableInstance.removeHook(name as keyof MilanoteHotEvents, callback);
            });
        };
    }, [tableInstance, session]);

    return session;
};
